import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { CloseButton, Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { PageHeader, Pagination, SelectSearch } from '../../components';
import CheckableDataTable from '../../components/CheckableDataTable/CheckableDataTable';
import SearchByDate from '../../components/SearchByDate/SearchByDate.components';
import useComponentState from './../../utils/useComponentState';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Card } from 'react-bootstrap';
import { Drawer } from 'rsuite';
import connectPrinter from '../../utils/connectPrinter';
import { getGreetlyVouchersByFilters, getTransactionDetails, handOverVoucher, setPrintDate, getGreetlyCsv } from '../../api/greetlyVouchers';
import { formatDateTime } from '../../utils/dataFormatter';
import { formatAmount } from '../../utils/amountFormatter';
import { DATE_TIME_TYPES } from '../../constants/dataTimeTypes';
import {
    ROLE_THYAGA_ADMINISTRATOR,
    ROLE_THYAGA_ACCOUNTANT,
    ROLE_THYAGA_MARKETING,
    ROLE_THYAGA_CUSTOMER_CARE,
    ROLE_MERCHANT_ADMINISTRATOR,
    ROLE_SUB_MERCHANT_ADMINISTRATOR,
} from '../../constants/roles';
import styles from './GreetlyVouchers.module.scss';
import PropTypes from 'prop-types';
import {
    INITIAL_STATE,
    BARISTA_TABLE_HEADING,
    SEARCH_DROPDOWN,
} from './greetlyVouchersConstants';
import {
    buildLabelObject,
    buildCheckedRowObject,
    getSearchNotFoundMessage,
    generateZPLCodes,
    findCheckedRowIndex,
    findVoucherByGreetlyId,
    deduplicateItems,
    filterOutByIds,
    toEndOfDay,
} from './greetlyVouchersUtils';


// searchKey (what the user picked) -> the field name the filter API expects.
const SEARCH_ID_MAP = {
    'GREETLY_ID': 'greetlyId',
    'VOUCHER_ID': 'voucherId',
    'THEME_NAME': 'themeName',
    'BATCH_ID': 'batchId',
    'STORE': 'store',
};

const SUB_SEARCH_ID_MAP = {
    'LOCATION': 'location',
};

// Only these roles see the Hand Over action on Greetly voucher listings;
// everyone else (including merchant / sub merchant admins) has it hidden.
const HAND_OVER_VISIBLE_ROLES = [
    ROLE_THYAGA_ADMINISTRATOR,
    ROLE_THYAGA_ACCOUNTANT,
    ROLE_THYAGA_MARKETING,
    ROLE_THYAGA_CUSTOMER_CARE,
];

// Merchant / sub merchant admins don't get the Print QR Codes action.
const PRINT_QR_HIDDEN_ROLES = [
    ROLE_MERCHANT_ADMINISTRATOR,
    ROLE_SUB_MERCHANT_ADMINISTRATOR,
];

const GreetlyVouchers = (props) => {
    const { pageTitle = 'Greetly Vouchers', storeFilter, locationFilter, tableVariant = 'default', role } = props;
    const showHandOver = HAND_OVER_VISIBLE_ROLES.includes(role);
    const showPrintQrCodes = !PRINT_QR_HIDDEN_ROLES.includes(role);
    // Barista and the merchant-facing page share the same reduced table: no
    // voucher id / top-up status columns.
    const isReducedTable =
        tableVariant === 'barista' || tableVariant === 'merchant';
    const [state, setState] = useComponentState({
        ...INITIAL_STATE,
        ...(isReducedTable ? { tableHeading: BARISTA_TABLE_HEADING } : {}),
        ...(storeFilter
            ? {
                // The store is fixed by storeFilter, so it can't be picked from
                // the dropdown; the search box starts on Greetly Voucher ID
                // instead, ready for an ID to be typed straight in.
                isClearable: false,
                searchDropDown: SEARCH_DROPDOWN.filter((item) => item.key !== 'STORE'),
            }
            : {}),
    });

    const {
        tableHeading,
        voucherData,
        loading,
        totalPages,
        currentPage,
        fetchError,
        selectAll,
        checkedRows,
        labelsToPrint,
        csvLoading,
        searchDropDown,
        subSearchDropDown,
        searchPlaceholder,
        subSearchPlaceholder,
        searchId,
        subSearchId,
        isVoucherTopup,
        isVoucherCreated,
        isHandedOver,
        startDate,
        endDate,
        notFoundMessage,
        showVoucherDrawer,
        drawerData,
        showTransactions,
        transactionDetails,
        transactionLoading,
        transactionError,
        selectedTransaction,
    } = state;

    // The filter API takes a single criterion. On store-scoped pages that
    // criterion is the store until the user types their own search term, so
    // the list stays limited to that store while the dropdown still starts on
    // Greetly Voucher ID.
    const getSearchCriteria = () => {
        if (storeFilter && !state.searchId) {
            return { searchId: SEARCH_ID_MAP.STORE, searchKey: storeFilter, pinned: true };
        }
        return {
            searchId: SEARCH_ID_MAP[state.searchKey] || state.searchKey,
            searchKey: state.searchId,
            pinned: false,
        };
    };

    // Once the user's own search term takes the single criterion slot, the store
    // is no longer part of the query, so send it separately and drop any rows
    // from other stores that still come back.
    const getStoreScope = (criteria) =>
        storeFilter && !criteria.pinned ? { store: storeFilter } : {};

    const isInStore = (row) =>
        !storeFilter ||
        (row.store || '').toLowerCase().includes(storeFilter.trim().toLowerCase());

    // Sub merchant administrators only ever see their own location, so it is
    // pinned rather than typed.
    const getSubSearchCriteria = () => {
        if (locationFilter) {
            return {
                subSearchId: SUB_SEARCH_ID_MAP.LOCATION,
                subSearchKey: locationFilter,
                pinned: true,
            };
        }
        return {
            subSearchId: SUB_SEARCH_ID_MAP[state.subSearchKey] || state.subSearchKey,
            subSearchKey: state.subSearchId,
            pinned: false,
        };
    };

    const filterByIdsApi = async (page, start, end) => {
        setState(prev => ({
            ...prev,
            loading: true,
            isFilterApiCalled: true,
        }));
        try {
            const criteria = getSearchCriteria();
            const subCriteria = getSubSearchCriteria();

            const params = {
                page,
                searchId: criteria.searchId,
                searchKey: criteria.searchKey,
                subSearchId: subCriteria.subSearchId,
                subSearchKey: subCriteria.subSearchKey,
                ...getStoreScope(criteria),
                startDate: start ? start : null,
                endDate: toEndOfDay(end),
                isVoucherCreated: isVoucherCreated,
                isVoucherTopup: isVoucherTopup,
                isHandedOver: isHandedOver,
            };

            const response = await getGreetlyVouchersByFilters(props.auth.auth.token, params);
            if (response.list) {
                const list = response.list.filter(isInStore);
                const notFoundMessage = list.length === 0
                    ? getSearchNotFoundMessage(state.searchKey, state.searchId, state.subSearchKey, state.subSearchId)
                    : null;

                setState(prev => ({
                    ...prev,
                    voucherData: list,
                    totalPages: response.totalPageCount || 0,
                    currentPage: response.pageNo || 0,
                    loading: false,
                    notFoundMessage,
                }));
            } else {
                setState(prev => ({
                    ...prev,
                    loading: false,
                    voucherData: [],
                    totalPages: 0,
                    currentPage: 0,
                    notFoundMessage: getSearchNotFoundMessage(state.searchKey, state.searchId),
                }));
            }
        } catch (err) {
            console.error('Error fetching Greetly vouchers by filter:', err);
            toast.error(err?.message || 'Failed to fetch vouchers');
            setState(prev => ({
                ...prev,
                loading: false,
                voucherData: [],
                totalPages: 0,
                currentPage: 0,
                notFoundMessage: getSearchNotFoundMessage(state.searchKey, state.searchId),
            }));
        }
    };

    const handlePrintCodes = async () => {
        const zpl = generateZPLCodes(checkedRows);

        try {
            const device = await connectPrinter();
            const encoder = new TextEncoder();
            const data = encoder.encode(zpl);

            await device.transferOut(1, data);

            const payload = {
                greetlyIds: checkedRows.map(row => row.greetlyId),
            };
            await setPrintDate(props.auth.auth.token, payload);
        } catch (error) {
            toast.error('Failed to print QR codes. Please check printer connection.');
        }
    };

    const handleDownloadCsv = async () => {
        setState(prev => ({ ...prev, csvLoading: true }));
        try {
            const criteria = getSearchCriteria();
            const subCriteria = getSubSearchCriteria();
            const payload = {
                page: state.currentPage,
                searchId: criteria.searchId,
                searchKey: criteria.searchKey,
                subSearchId: subCriteria.subSearchId,
                subSearchKey: subCriteria.subSearchKey,
                ...getStoreScope(criteria),
                startDate: state.startDate || null,
                endDate: toEndOfDay(state.endDate),
                isVoucherCreated: state.isVoucherCreated,
                isVoucherTopup: state.isVoucherTopup,
                isHandedOver: state.isHandedOver,
            };
            const blob = await getGreetlyCsv(props.auth.auth.token, payload);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'greetly-vouchers.csv';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Failed to download CSV. Please try again.');
        } finally {
            setState(prev => ({ ...prev, csvLoading: false }));
        }
    };

    const handleHandOverVouchers = async () => {
        if (checkedRows.length === 0) {
            toast.error('No vouchers selected for hand over');
            return;
        }

        try {
            const payload = {
                greetlyIds: checkedRows.map(row => row.greetlyId),
            };

            setState(prev => ({ ...prev, loading: true }));
            const response = await handOverVoucher(props.auth.auth.token, payload);

            if (response && response.success) {
                toast.success('Vouchers handed over successfully');
                // Reset selection after successful hand over
                setState(prev => ({
                    ...prev,
                    checkedRows: [],
                    labelsToPrint: [],
                    selectAll: false,
                    loading: false,
                }));
                // Refresh with the filters that are still shown on screen
                filterByIdsApi(state.currentPage, state.startDate, state.endDate);
            } else {
                const errorMessage = response?.message || 'Failed to hand over vouchers';
                toast.error(errorMessage);
                setState(prev => ({ ...prev, loading: false }));
            }
        } catch (error) {
            toast.error('Error during over vouchers');
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    const handlePagination = (page) => {
        filterByIdsApi(page, state.startDate, state.endDate);
    };

    const handleIdSearch = () => {
        const { searchId, subSearchId } = state;
        if (searchId || subSearchId) {
            setState({
                isSearchApiCalled: true,
                isFilterApiCalled: false,
                isClearable: !!searchId,
                isClearableSub: !!subSearchId,
            });
            filterByIdsApi(0, state.startDate, state.endDate);
        }
    };

    // Clearing either search resets every filter (both searches, dates and
    // status). Bumping resetCount makes the fetch effect reload with the
    // cleared state on the next render.
    const handleResetAllFilters = () => {
        setState((prev) => ({
            ...prev,
            searchId: '',
            subSearchId: '',
            isSearchApiCalled: false,
            isClearable: false,
            isClearableSub: false,
            startDate: null,
            endDate: null,
            isVoucherTopup: false,
            isVoucherCreated: false,
            isHandedOver: false,
            notFoundMessage: null,
            checkedRows: [],
            labelsToPrint: [],
            resetCount: (prev.resetCount || 0) + 1,
        }));
    };

    const searchOnChange = (event) => {
        setState({
            searchId: event.target.value,
            isClearable: false,
        });
    };

    const subSearchOnChange = (event) => {
        setState({
            subSearchId: event.target.value,
            isClearableSub: false,
        });
    };

    const onChangeStartDate = (startDateVal) => {
        const { endDate: endDateVal } = state;
        setState({ startDate: startDateVal });
        if (startDateVal && endDateVal) {
            setState({ isFilterApiCalled: true });
            filterByIdsApi(0, startDateVal, endDateVal);
        }
        if (!startDateVal && !endDateVal) {
            filterByIdsApi(0, null, null);
        }
    };

    const onChangeEndDate = (endDateVal) => {
        const { startDate: startDateVal } = state;
        setState({ endDate: endDateVal });
        if (startDateVal && endDateVal) {
            setState({ isFilterApiCalled: true });
            filterByIdsApi(0, startDateVal, endDateVal);
        }
        if (!startDateVal && !endDateVal) {
            filterByIdsApi(0, null, null);
        }
    };

    const handleSearchDropDownOnClick = (key, label) => {
        setState({
            searchPlaceholder: label,
            searchKey: key,
            searchId: '',
            isClearable: false,
        });
    };

    const handleSubSearchDropDownOnClick = (key, label) => {
        setState({
            subSearchPlaceholder: label,
            subSearchKey: key,
            subSearchId: '',
            isClearableSub: false,
        });
    }

    const renderMultipleSearch = () => {
        const selected = searchDropDown.find((item) => item.label === searchPlaceholder);
        return (
            <SelectSearch
                options={searchDropDown}
                selectedKey={selected ? selected.key : state.searchKey}
                onSelectChange={handleSearchDropDownOnClick}
                value={searchId}
                onChange={(value) => searchOnChange({ target: { value } })}
                onSearch={handleIdSearch}
                onClear={handleResetAllFilters}
                selectWidth={190}
                placeholder={searchPlaceholder}
                maxLength={50}
            />
        );
    };

    const renderSubSearch = () => {
        // Location is fixed to the logged-in sub merchant, so there is nothing
        // left to pick or type here.
        if (locationFilter) return null;

        const selected = subSearchDropDown.find((item) => item.label === subSearchPlaceholder);
        return (
            <SelectSearch
                options={subSearchDropDown}
                selectedKey={selected ? selected.key : state.subSearchKey}
                onSelectChange={handleSubSearchDropDownOnClick}
                value={subSearchId}
                onChange={(value) => subSearchOnChange({ target: { value } })}
                onSearch={handleIdSearch}
                onClear={handleResetAllFilters}
                placeholder={subSearchPlaceholder}
                maxLength={40}
            />
        );
    };


    const handleCheckBoxToggle = (rowId, event) => {
        event.stopPropagation();
        handleCheck(rowId);
    };

    const handleCheck = (rowId) => {
        // rowId is greetlyId
        setState((prev) => {
            const existingIndex = findCheckedRowIndex(prev.checkedRows, rowId);
            let newChecked = [...prev.checkedRows];
            let labels = [...prev.labelsToPrint];

            if (existingIndex > -1) {
                // remove
                const removed = newChecked.splice(existingIndex, 1)[0];
                labels = labels.filter((lbl) => lbl.id !== removed.voucherId);
            } else {
                // add
                const data = findVoucherByGreetlyId(voucherData, rowId);
                if (data) {
                    newChecked.push(buildCheckedRowObject(data));
                    // labels.push(buildLabelObject(data));
                }
            }

            // Determine if all rows are now selected
            const allSelected = voucherData.length > 0 && voucherData.every((v) =>
                newChecked.some((c) => c.greetlyId === v.greetlyId)
            );

            return {
                ...prev,
                selectAll: allSelected,
                checkedRows: newChecked,
                labelsToPrint: labels,
            };
        });
    };

    const handleMultipleCheck = (ids) => {
        // ids are greetlyIds
        setState((prev) => {
            let newChecked = [...prev.checkedRows];
            let labels = [...prev.labelsToPrint];

            ids.forEach((id) => {
                const existingIndex = findCheckedRowIndex(newChecked, id);
                if (existingIndex > -1) {
                    const removed = newChecked.splice(existingIndex, 1)[0];
                    labels = labels.filter((lbl) => lbl.id !== removed.voucherId);
                } else {
                    const data = findVoucherByGreetlyId(voucherData, id);
                    if (data) {
                        newChecked.push(buildCheckedRowObject(data));
                        labels.push(buildLabelObject(data));
                    }
                }
            });

            // Determine if all rows are now selected
            const allSelected = voucherData.length > 0 && voucherData.every((v) =>
                newChecked.some((c) => c.greetlyId === v.greetlyId)
            );

            return {
                ...prev,
                selectAll: allSelected,
                checkedRows: newChecked,
                labelsToPrint: labels,
            };
        });
    };

    const handleSelectAll = (event) => {
        const checked = event.target.checked;
        if (checked) {
            const selectedAll = voucherData.map(buildCheckedRowObject);
            const newLabels = voucherData.map(buildLabelObject);
            setState((prev) => ({
                selectAll: true,
                checkedRows: deduplicateItems(
                    [...prev.checkedRows, ...selectedAll],
                    'greetlyId'
                ),
                labelsToPrint: deduplicateItems(
                    [...prev.labelsToPrint, ...newLabels],
                    'id'
                ),
            }));
        } else {
            const voucherGreetlyIds = new Set(voucherData.map((d) => d.greetlyId));
            const voucherIds = new Set(voucherData.map((d) => d.voucher.id));
            setState({
                checkedRows: filterOutByIds(checkedRows, voucherGreetlyIds, 'greetlyId'),
                labelsToPrint: filterOutByIds(labelsToPrint, voucherIds, 'id'),
            });
        }
    };

    const handleOnClick = (greetlyId) => {
        // Toggle checkbox
        setState(prev => {
            const existingIndex = findCheckedRowIndex(prev.checkedRows, greetlyId);
            let newChecked = [...prev.checkedRows];

            if (existingIndex > -1) {
                newChecked.splice(existingIndex, 1);
            } else {
                const data = findVoucherByGreetlyId(voucherData, greetlyId);
                if (data) newChecked.push(buildCheckedRowObject(data));
            }

            const allSelected = voucherData.length > 0 && voucherData.every(v =>
                newChecked.some(c => c.greetlyId === v.greetlyId)
            );

            return {
                ...prev,
                checkedRows: newChecked,
                selectAll: allSelected,
            };
        });

        // Open drawer as before
        const selectedVoucher = voucherData.find(v => v.greetlyId === greetlyId);
        if (selectedVoucher) {
            setState(prev => ({
                ...prev,
                showVoucherDrawer: true,
                drawerData: selectedVoucher,
                voucherId: selectedVoucher.voucher.id,
            }));
        }
    };


    // Handler to close drawer and reset state
    const handleDrawerOnHide = () => {
        setState({
            showVoucherDrawer: false,
            drawerData: null,
            voucherId: null,
            showTransactions: false,
            transactionDetails: [],
            selectedTransaction: null,
            transactionError: '',
        });
    };

    // Get transaction details for the selected voucher
    const getTransactionDetailsForVoucher = async (greetlyId) => {
        setState(prev => ({ ...prev, transactionLoading: true, transactionError: '' }));
        try {
            const response = await getTransactionDetails(props.auth.auth.token, greetlyId);
            if (response && response.greetlyTransactionList && Array.isArray(response.greetlyTransactionList)) {
                setState(prev => ({
                    ...prev,
                    transactionDetails: response.greetlyTransactionList,
                    transactionLoading: false,
                }));
            } else {
                setState(prev => ({
                    ...prev,
                    transactionDetails: [],
                    transactionLoading: false,
                    transactionError: 'Failed to fetch transaction details',
                }));
            }
        } catch (error) {
            toast.error('Failed to fetch transaction details');
            setState(prev => ({
                ...prev,
                transactionLoading: false,
                transactionError: 'Error fetching transaction details',
            }));
        }
    };

    // Handler for viewing transactions in drawer - fetches transactions when button is clicked
    const handleShowTransactions = () => {
        if (!showTransactions) {
            // Show transactions and fetch data
            setState(prev => ({
                ...prev,
                transactionLoading: true,
            }));
            getTransactionDetailsForVoucher(drawerData.greetlyId);
            setState(prev => ({
                ...prev,
                showTransactions: true,
                selectedTransaction: null,
            }));
        } else {
            // Hide transactions and go back to voucher details
            setState(prev => ({
                ...prev,
                showTransactions: false,
                selectedTransaction: null,
            }));
        }
    };

    // Handler for clicking on a transaction row
    const handleTransactionRowClick = (transaction) => {
        setState({ selectedTransaction: transaction });
    };

    // Handler to go back from transaction details
    const handleTransactionGoBack = () => {
        setState({
            showTransactions: false,
            selectedTransaction: null,
        });
    };

    // Only one status filter can be ticked at a time: ticking one clears the
    // others, and clicking the ticked one clears it.
    const handleFilterCheckboxChange = (key) => {
        setState((prev) => ({
            ...prev,
            isVoucherTopup: key === 'isVoucherTopup' && !prev.isVoucherTopup,
            isVoucherCreated: key === 'isVoucherCreated' && !prev.isVoucherCreated,
            isHandedOver: key === 'isHandedOver' && !prev.isHandedOver,
        }));
    }


    useEffect(() => {
        setState({ currentPage: 0 });
        filterByIdsApi(0, state.startDate, state.endDate);
    }, [state.isVoucherTopup, state.isVoucherCreated, state.isClearable, state.isClearableSub, state.isHandedOver, state.resetCount]);

    // Render transaction details in drawer
    const renderTransactionDetails = (data) => {
        if (!data) return null;
        return (
            <div className={`details_group ${styles.content}`}>
                <Card>
                    <Card.Body>
                        <div className='detail_group_content'>
                            <span className='content_header'>Description</span>
                            <span className='content_value'>{data.description || 'N/A'}</span>
                        </div>
                        <div className='detail_group_content'>
                            <span className='content_header'>Amount</span>
                            <span className='content_value'>{formatAmount(data.amount) || 'N/A'}</span>
                        </div>
                        <div className='detail_group_content'>
                            <span className='content_header'>Date</span>
                            <span className='content_value'>{formatDateTime(data.transactionDate, DATE_TIME_TYPES.ISO_DATE_TIME) || 'N/A'}</span>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    };

    // Render Greetly voucher details in drawer
    const renderVoucherDetailsInDrawer = (data) => {
        if (!data) return null;
        const voucher = data.voucher;

        const primaryContent = [
            {
                label: 'Greetly ID',
                value: data.greetlyId,
            },
            {
                label: 'Voucher ID',
                value: voucher.id,
            },
            {
                label: 'Theme',
                value: voucher.voucherTheme?.imageFront ? (
                    <img
                        src={voucher.voucherTheme.imageFront}
                        alt='Voucher Theme'
                        style={{ maxWidth: '200px', borderRadius: '8px' }}
                    />
                ) : 'N/A',
            },
            {
                label: 'Amount',
                value: formatAmount(voucher.amount),
            },
            {
                label: 'Status',
                value: voucher.status || 'N/A',
            },
            {
                label: 'Created Date',
                value: formatDateTime(voucher.createdDate, DATE_TIME_TYPES.ISO_DATE_TIME),
            },
            {
                label: 'Expiry Date',
                value: voucher.expiryDate ? formatDateTime(voucher.expiryDate, DATE_TIME_TYPES.ISO_DATE_TIME) : 'N/A',
            },
            {
                label: 'Batch ID',
                value: data.batchId || 'N/A',
            },
            {
                label: 'Store',
                value: data.store || 'N/A',
            },
            {
                label: 'Handed Over At',
                value: data.handedOverAt ? formatDateTime(data.handedOverAt, DATE_TIME_TYPES.ISO_DATE_TIME) : 'N/A',
            }
        ];

        return (
            <div className={`details_group ${styles.content}`}>
                <Card>
                    <Card.Body>
                        {primaryContent.map((item, index) => (
                            <div key={index} className='detail_group_content'>
                                <span className='content_header'>{item.label}</span>
                                <span className='content_value'>{item.value}</span>
                            </div>
                        ))}
                    </Card.Body>
                </Card>
            </div>
        );
    };

    // Render the drawer component
    const renderDrawer = () => {
        if (!drawerData) return null;

        return (
            <div className='purchase_drawer_root'>
                <div className='drawer_child'>
                    <div className='header_group'>
                        <span className='header_title'>Greetly Voucher ID</span>
                        <span className='header_value'>{drawerData.greetlyId}</span>
                    </div>

                    {selectedTransaction ? (
                        renderTransactionDetails(selectedTransaction)
                    ) : (
                        <>
                            {showTransactions ? (
                                <div className={`details_group ${styles.content}`}>
                                    {transactionLoading ? (
                                        <div className={styles.loadingContainer}>
                                            <FontAwesomeIcon icon={faSpinner} spin /> Loading transactions...
                                        </div>
                                    ) : transactionDetails.length > 0 ? (
                                        <div className={styles.transactionsWrapper}>
                                            <table className={styles.transactionsTable}>
                                                <thead className={styles.transactionsTableHeader}>
                                                    <tr className={styles.transactionTableRow}>
                                                        <th className={styles.transactionHeader}>Description</th>
                                                        <th className={styles.transactionHeader}>Date</th>
                                                        <th className={styles.transactionHeader}>Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {transactionDetails.map((transaction, index) => (
                                                        <tr
                                                            key={index}
                                                            style={{
                                                                borderBottom: '1px solid #ddd',
                                                                cursor: 'pointer',
                                                                backgroundColor: selectedTransaction === transaction ? '#f0f0f0' : 'white',
                                                            }}
                                                            onClick={() => handleTransactionRowClick(transaction)}
                                                        >
                                                            <td style={{ padding: '10px' }}>{transaction.description || 'N/A'}</td>
                                                            <td style={{ padding: '10px' }}>{formatDateTime(transaction.transactionDate, DATE_TIME_TYPES.ISO_DATE_TIME)}</td>
                                                            <td style={{ padding: '10px', textAlign: 'right' }}>{formatAmount(transaction.amount)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className={styles.noTransactionsMessage}>
                                            {transactionError || 'No transactions found'}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                renderVoucherDetailsInDrawer(drawerData)
                            )}
                        </>
                    )}

                    <div className={`footer ${styles.footerContent}`}>
                        <div className={`button-container ${styles.buttonGroup}`}>
                            {selectedTransaction ? (
                                <Button
                                    className={styles.buttonFlex}
                                    onClick={() => handleTransactionGoBack()}
                                >
                                    Go Back
                                </Button>
                            ) : (
                                <Button
                                    className={styles.buttonFlex}
                                    onClick={handleShowTransactions}
                                    disabled={transactionLoading}
                                >
                                    {showTransactions ? 'View Greetly Details' : 'View Transactions'}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.root_div}>
            <div>
                <PageHeader text={pageTitle} />

                <div className={styles.toolbar}>
                    <div className={styles.toolbar_row}>
                        <div className={styles.filter_search}>{renderMultipleSearch()}</div>
                        {!locationFilter && (
                            <div className={styles.filter_search}>{renderSubSearch()}</div>
                        )}
                        <div className={styles.filter_date}>
                            <SearchByDate
                                onStartDateChange={onChangeStartDate}
                                onEndDateChange={onChangeEndDate}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        </div>
                    </div>

                    <div className={`${styles.toolbar_row} ${styles.toolbar_row_secondary}`}>
                        <div className={styles.status_group} role='group' aria-label='Filter by status'>
                            <span className={styles.status_label}>Status</span>
                            {[
                                { key: null, label: 'All', active: !state.isVoucherTopup && !state.isVoucherCreated && !state.isHandedOver },
                                { key: 'isVoucherTopup', label: 'Topped Up', active: state.isVoucherTopup },
                                { key: 'isVoucherCreated', label: 'Voucher Created', active: state.isVoucherCreated },
                                { key: 'isHandedOver', label: 'Handed Over', active: state.isHandedOver },
                            ].map(({ key, label, active }) => (
                                <button
                                    key={label}
                                    type='button'
                                    className={`${styles.status_pill} ${active ? styles.status_pill_active : ''}`}
                                    aria-pressed={active}
                                    onClick={() => handleFilterCheckboxChange(key)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div className={styles.toolbar_actions}>
                            {checkedRows.length > 0 && (
                                <span className={styles.selected_chip}>
                                    {checkedRows.length} voucher
                                    {checkedRows.length > 1 ? 's' : ''} selected
                                    <CloseButton
                                        className={styles.selected_chip_close}
                                        aria-label='Clear selection'
                                        onClick={() => {
                                            setState({ checkedRows: [], labelsToPrint: [] });
                                        }}
                                    />
                                </span>
                            )}
                            {showPrintQrCodes && (
                                <Dropdown>
                                    <Dropdown.Toggle
                                        disabled={checkedRows.length === 0}
                                        variant='primary'
                                        id='dropdown-basic'
                                    >
                                        Print QR Codes
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => handlePrintCodes()}>
                                            Print with Zebra
                                        </Dropdown.Item>

                                    </Dropdown.Menu>
                                </Dropdown>
                            )}

                            {showHandOver && (
                                <Dropdown>
                                    <Dropdown.Toggle
                                        disabled={checkedRows.length === 0 || loading}
                                        variant="primary"
                                        id="handover-btn"
                                        className={styles.noCaret}
                                        onClick={handleHandOverVouchers}   // behave like normal button
                                    >
                                        Hand Over
                                    </Dropdown.Toggle>

                                    {/* Empty menu (not displayed) */}
                                    <Dropdown.Menu className={styles.hiddenMenu}></Dropdown.Menu>
                                </Dropdown>
                            )}
                            <Button
                                variant='outline-secondary'
                                onClick={handleDownloadCsv}
                                disabled={csvLoading}
                                className={styles.download_btn}
                            >
                                {csvLoading ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Downloading...</>
                                ) : (
                                    'Download CSV'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className='table-fit'>
                    <CheckableDataTable
                        tableData={voucherData}
                        tableheading={tableHeading}
                        tag={isReducedTable ? 'barista-greetly-vouchers' : 'greetly-vouchers'}
                        fetchError={notFoundMessage || fetchError}
                        notFoundMessage={notFoundMessage || fetchError}
                        search_filter_loading={loading}
                        handleCheck={handleCheckBoxToggle}
                        handleSelectAll={handleSelectAll}
                        handleMultipleCheck={() => { }}
                        handleOnClick={handleOnClick}
                        searchFilterLoading={loading}
                        checkedRows={checkedRows.map((c) => c.greetlyId)}
                        selectAll={selectAll}
                    />
                    {!loading && !notFoundMessage && !fetchError && (
                        <Pagination
                            pageCount={totalPages}
                            onChange={handlePagination}
                            currentPage={currentPage}
                        />
                    )}
                </div>

                {/* Drawer for Greetly Voucher Details */}
                <Drawer show={showVoucherDrawer} onHide={handleDrawerOnHide}>
                    <Drawer.Header closeButton>
                        <Drawer.Title>Greetly Voucher Details</Drawer.Title>
                    </Drawer.Header>
                    <Drawer.Body>
                        {renderDrawer()}
                    </Drawer.Body>
                </Drawer>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        auth: state.auth,
        role: state.auth.auth.details.role,
    };
};

GreetlyVouchers.propTypes = {
    auth: PropTypes.shape({
        auth: PropTypes.shape({
            token: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
    role: PropTypes.string,
    pageTitle: PropTypes.string,
    storeFilter: PropTypes.string,
    locationFilter: PropTypes.string,
    tableVariant: PropTypes.string,
};

export default connect(mapStateToProps)(GreetlyVouchers);
